import {testIdProps} from '@github-ui/test-id-props'
import {ArchiveIcon, IssueOpenedIcon, TrashIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {memo, use, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {flushSync} from 'react-dom'

import {ItemType} from '../../api/memex-items/item-type'
import {TableRowActionMenuUI} from '../../api/stats/contracts'
import {RowDraggerMenuUI} from '../../api/stats/contracts/common'
import {DraftConvert} from '../../api/stats/contracts/draft'
import {isMemexItemTypeArchivable} from '../../helpers/archive-util'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {rowIsSelected} from '../../hooks/tanstack-table/helpers/row-api'
import type {Row} from '../../hooks/tanstack-table/helpers/types'
import {
  useSetRowSelection,
  useTableDeselectAllRows,
  useTableSelectedFlatRows,
} from '../../hooks/tanstack-table/hooks/use-table-row-selection'
import {useTableInstance, useTableRowMenuShortcutOrigin} from '../../hooks/tanstack-table/providers/table-provider'
import {useArchiveMemexItemsWithConfirmation} from '../../hooks/use-archive-memex-items-with-confirmation'
import {useRemoveMemexItemWithConfirmation} from '../../hooks/use-remove-memex-items-with-id'
import type {MemexItemModel} from '../../models/memex-item-model'
import {createSetFocusStateAction} from '../../navigation/context'
import {delKey as DelKey, eKey as EKey} from '../common/keyboard-shortcuts'
import {getMovableRows} from '../move-item/move-dialog/move-dialog'
import {MoveItem} from '../move-item/move-item'
import {RepoPicker} from '../repo-picker'
import {BaseCell} from './cells/base-cell'
import {clearFocus, useStableTableNavigation} from './navigation'
import styles from './row-dragger.module.css'
import {getIsSubIssue} from './row-reordering/helpers/get-is-sub-issue'
import {useSetRowMenuOpen} from './state-providers/row-menu-shortcut-provider'
import {RowContext} from './table-row'
import {isRowSelectedAndActionable, useRowSelection} from './use-row-selection'

export const ROW_DRAG_HANDLE_WIDTH = 60

// disable dragging for RedactedItems
const DefaultCellStyle: React.CSSProperties = {
  position: 'relative',
  overflow: 'visible',
  userSelect: 'none',
  WebkitUserSelect: 'none',
}

/**
 * A handle that can be used to drag its containing row up and down in the
 * table view.
 */
function RowDragger({row}: {row: Row<MemexItemModel>}) {
  const deselectAllRows = useTableDeselectAllRows()
  const table = useTableInstance()
  const topLevelRows = table.getPreExpandedRowModel().rows
  const {setRowMenuOpen} = useSetRowMenuOpen()
  const selectedRows = useTableSelectedFlatRows()
  const setRowSelection = useSetRowSelection()
  const {navigationDispatch} = useStableTableNavigation()
  const rowMenuShortcutOrigin = useTableRowMenuShortcutOrigin()
  const {postStats} = usePostStats()
  const {contentType} = row.original
  const {rowNumber, setSortActivatorNodeRef, sortListeners} = use(RowContext)
  const [menuOpen, setMenuOpen] = useState(false)
  const [repoPickerOpen, setRepoPickerOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const postponeFocusResetRef = useRef(false)
  const {hasWritePermissions} = ViewerPrivileges()
  const baseRef = useRef<HTMLDivElement | null>(null)
  const numberRef = useRef<HTMLDivElement>(null)
  // ignore clicks on the ActionMenu (action menu doesn't currently support ref)
  const clickTargets = useMemo(() => [baseRef, numberRef], [baseRef, numberRef])
  const {onRowClick: onClick, onRowPointerDown, focusLastRowIndex} = useRowSelection(row, clickTargets)
  const isSubIssue = getIsSubIssue(row)
  const movableRows = getMovableRows(topLevelRows, row)

  const resetMenuFocus = useCallback(() => {
    if (rowMenuShortcutOrigin) {
      navigationDispatch(createSetFocusStateAction(rowMenuShortcutOrigin))
    }
    setRowMenuOpen()
  }, [rowMenuShortcutOrigin, navigationDispatch, setRowMenuOpen])

  const openMenu = useCallback(() => {
    // eslint-disable-next-line @eslint-react/dom/no-flush-sync
    flushSync(() => {
      navigationDispatch(clearFocus())
      setMenuOpen(true)
    })
  }, [navigationDispatch, setMenuOpen])

  const closeMenu = useCallback(() => {
    if (!postponeFocusResetRef.current) {
      resetMenuFocus()
    }

    setMenuOpen(false)
  }, [postponeFocusResetRef, resetMenuFocus, setMenuOpen])

  const setMenuState = useCallback((state: boolean): void => (state ? openMenu() : closeMenu()), [openMenu, closeMenu])

  const onOpen = useCallback(() => {
    postponeFocusResetRef.current = true

    // if the current row is already in the selection, do nothing
    // otherwise select only the current row
    if (!rowIsSelected(row)) {
      setRowSelection([row.id])
    }
  }, [row, setRowSelection])

  const onDismiss = useCallback(() => {
    postponeFocusResetRef.current = false
    closeMenu()
    buttonRef.current?.focus()
  }, [closeMenu])

  const onConfirm = useCallback(
    (removedItemIds: Array<number>) => {
      postponeFocusResetRef.current = false
      resetMenuFocus()
      deselectAllRows()
      focusLastRowIndex(removedItemIds)
    },
    [resetMenuFocus, deselectAllRows, focusLastRowIndex],
  )

  useEffect(() => {
    if (rowMenuShortcutOrigin?.details.y === row.id && contentType !== ItemType.RedactedItem) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMenuState(true)
    }
  }, [row.id, setMenuState, rowMenuShortcutOrigin?.details.y, contentType])

  const {openRemoveConfirmationDialog} = useRemoveMemexItemWithConfirmation(onOpen, onConfirm, onDismiss)
  const {openArchiveConfirmationDialog} = useArchiveMemexItemsWithConfirmation(onOpen, onConfirm, onDismiss)

  const deleteOnClick = useCallback(() => {
    openRemoveConfirmationDialog([row.original.id], TableRowActionMenuUI)
  }, [openRemoveConfirmationDialog, row.original.id])

  const archiveOnClick = useCallback(() => {
    openArchiveConfirmationDialog([row.original.id], TableRowActionMenuUI)
  }, [openArchiveConfirmationDialog, row.original.id])

  const archiveMultipleOnClick = useCallback(() => {
    const selectedItemIds = selectedRows.map(r => r.original.id)
    openArchiveConfirmationDialog(selectedItemIds, TableRowActionMenuUI)
  }, [openArchiveConfirmationDialog, selectedRows])

  const deleteMultipleOnClick = useCallback(() => {
    const selectedItemIds = selectedRows.map(r => r.original.id)
    openRemoveConfirmationDialog(selectedItemIds, TableRowActionMenuUI)
  }, [openRemoveConfirmationDialog, selectedRows])

  const isSelected = isRowSelectedAndActionable(row)
  const multipleSelected = isSelected && selectedRows.length > 1
  const isArchivable =
    selectedRows.every(selectedRow => isMemexItemTypeArchivable(selectedRow.original.contentType)) &&
    isMemexItemTypeArchivable(contentType) &&
    hasWritePermissions

  const onRowConvertSuccess = useCallback(() => {
    const itemId = row.original.id
    postStats({
      name: DraftConvert,
      ui: RowDraggerMenuUI,
      memexProjectItemId: itemId,
    })
  }, [postStats, row])

  const cellProps = useMemo<React.HTMLAttributes<HTMLDivElement>>(() => {
    if (contentType === ItemType.RedactedItem || !hasWritePermissions) {
      return {
        style: {...DefaultCellStyle, cursor: 'default'},
        title: 'Reordering this item is disabled',
        onClick,
        // enabling the mousedown event handler for this path to ensure that
        // multiple rows can be selected when the table is sorted
        onPointerDown: undefined,
        ref: baseRef,
      }
    }

    return {
      draggable: true,
      style: DefaultCellStyle,
      onClick,
      ref: (ref: HTMLDivElement | null) => {
        setSortActivatorNodeRef?.(ref)
        baseRef.current = ref
      },
      onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
        onRowPointerDown()

        // invoke dnd-kit pointer down handler
        sortListeners?.onPointerDown?.(event)
      },
    }
  }, [contentType, hasWritePermissions, onClick, setSortActivatorNodeRef, onRowPointerDown, sortListeners])

  return (
    <BaseCell {...cellProps} className={styles.BaseCell} {...testIdProps('row-dragger')}>
      {/* Only show row numbers for top-level issues, not for sub-issues */}
      {!isSubIssue && (
        <div ref={numberRef} className={styles.Box} aria-hidden="true">
          {rowNumber}
        </div>
      )}
      {hasWritePermissions && contentType !== ItemType.RedactedItem && (
        <>
          <ActionMenu open={menuOpen} onOpenChange={setMenuState} anchorRef={buttonRef}>
            <ActionMenu.Anchor>
              <IconButton
                ref={buttonRef}
                icon={TriangleDownIcon}
                aria-label="Row actions"
                /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                className={clsx('menu-trigger', styles.IconButton)}
                tabIndex={-1}
                block
                {...testIdProps('row-menu-trigger')}
              />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay {...testIdProps('row-menu')}>
              <ActionList>
                {!multipleSelected && contentType === ItemType.DraftIssue ? (
                  <ActionList.Item
                    onSelect={() => setRepoPickerOpen(true)}
                    {...testIdProps('row-menu-convert-to-issue')}
                  >
                    <ActionList.LeadingVisual>
                      <IssueOpenedIcon />
                    </ActionList.LeadingVisual>
                    Convert to issue
                  </ActionList.Item>
                ) : null}

                {isArchivable ? (
                  <>
                    {multipleSelected ? (
                      <ActionList.Item onSelect={archiveMultipleOnClick} {...testIdProps('row-menu-archive-multiple')}>
                        <ActionList.LeadingVisual>
                          <ArchiveIcon />
                        </ActionList.LeadingVisual>
                        Archive {selectedRows.length} items
                        <ActionList.TrailingVisual>
                          <EKey />
                        </ActionList.TrailingVisual>
                      </ActionList.Item>
                    ) : (
                      <ActionList.Item onSelect={archiveOnClick} {...testIdProps('row-menu-archive')}>
                        <ActionList.LeadingVisual>
                          <ArchiveIcon />
                        </ActionList.LeadingVisual>
                        Archive
                        <ActionList.TrailingVisual>
                          <EKey />
                        </ActionList.TrailingVisual>
                      </ActionList.Item>
                    )}
                  </>
                ) : null}

                {multipleSelected ? (
                  <ActionList.Item
                    variant="danger"
                    onSelect={deleteMultipleOnClick}
                    {...testIdProps('row-menu-delete-multiple')}
                  >
                    <ActionList.LeadingVisual>
                      <TrashIcon />
                    </ActionList.LeadingVisual>
                    Delete {selectedRows.length} selected items from project
                    <ActionList.TrailingVisual>
                      <DelKey />
                    </ActionList.TrailingVisual>
                  </ActionList.Item>
                ) : (
                  <ActionList.Item variant="danger" onSelect={deleteOnClick} {...testIdProps('row-menu-delete')}>
                    <ActionList.LeadingVisual>
                      <TrashIcon />
                    </ActionList.LeadingVisual>
                    Remove from project
                    <ActionList.TrailingVisual>
                      <DelKey />
                    </ActionList.TrailingVisual>
                  </ActionList.Item>
                )}
                {movableRows.length > 1 && (
                  <>
                    <ActionList.Divider />
                    <MoveItem selectedRow={row} />
                  </>
                )}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
          <RepoPicker
            anchorRef={baseRef}
            isOpen={repoPickerOpen}
            item={row.original}
            onOpenChange={(isOpen: boolean) => setRepoPickerOpen(isOpen)}
            onSuccess={onRowConvertSuccess}
            {...testIdProps('row-menu-repo-picker')}
          />
        </>
      )}
    </BaseCell>
  )
}
export default memo(RowDragger)
